import Wizard from '../components/wizard';
import React, { useEffect, useState } from 'react';
import Header from '../layouts/Header';
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Form, Container, Row, Col, Card, Badge } from 'react-bootstrap';
import DoctorCard from '../components/card/DoctorsCard';
import useManageAppointment from '../hooks/patientDashboard/useManageAppointment';
import useManagePatient from '../hooks/patientDashboard/useManagePatient';
import useManageDashboardOverview from '../hooks/patientDashboard/usemanageDashboardData';
import moment from 'moment';
import useProfileStore from '../store/patient/profileStore';
import Spinner1 from '../components/common/spinners';
import { 
    Calendar, 
    Users, 
    Clock, 
    Video,
    MapPin,
    Stethoscope,
    UserCircle,
    FileText,
    AlertCircle,
    XCircle,
    CheckCircle2,
    CalendarDays,
    ArrowLeft,
    Edit2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Schedule = () => {
    // State management
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const [startDate, setStartDate] = useState();
    const [selectedDoctor, setSelectedDoctor] = useState();
    const [selectedDept, setSelectedDept] = useState();
    const [selectedComplaints, setSelectedComplaints] = useState({});
    const [selectedTime, setSelectedTime] = useState();
    const [description, setDescription] = useState("");
    const [step, setStep] = useState("consultationType");
    const [selectedService, setSelectedService] = useState();
    const [isVirtual, setIsVirtual] = useState()
    const navigate = useNavigate();

    // Hooks
    const { loading, loadingDr, doctors, handleFetchAllDoctors, handleBookAppointment, departments, handleCheckAvailability, handleFetchAllDepartments, booking } = useManageAppointment();
    const { currentUser, findPatient } = useManagePatient();
    const { profile } = useProfileStore();
    const { appointments, loading: onLoad, handleFetAppointments, handleCancelAppointment } = useManageDashboardOverview();

    
    const groupedDoctors = doctors?.availableDoctors?.map(doctor => {
        const doctorTimeSlots = doctors?.availableTimeSlots
            .filter(slot => slot?.doctor?.id === doctor?.id)
            .map(({ doctor, ...slot }) => slot);
        
        return {
            ...doctor,
            availableTimeSlots: doctorTimeSlots
        };
    });

    // Effects
    useEffect(() => {
        handleFetchAllDepartments(profile?.id);
        findPatient();
        handleFetAppointments();
    }, []);

    // Handlers
    const handleDateSelect = (date) => {
        setStartDate(date);
        setStep("selectDepartment");
    };

    const handleDepartmentSelect = (department) => {
        setSelectedDept(department);
        const availabilityData = {
            departmentId: department?.id,
            date: moment(startDate).format("YYYY-MM-DD"),
            includeTimeSlots: true
        };
        handleCheckAvailability(availabilityData);
        setStep('services')
    };

    const handleServiceSelect = (service) => {
        setSelectedService(service);
        setStep("selectDoctor");
    };

    const handleDoctorSelect = (doctor) => {
        setSelectedDoctor(doctor);
    };

    const handleContinueToForm = () => {
        setStep("fillForm");
    };

    const handleBack = () => {
        const stepOrder = ["consultationType", "start", "selectDepartment", "services", "selectDoctor", "fillForm"];
        const currentIndex = stepOrder.indexOf(step);
        if (currentIndex > 0) {
            setStep(stepOrder[currentIndex - 1]);
        }
    };

    // New function to jump to any step
    const handleStepClick = (targetStep) => {
        const stepOrder = ["consultationType", "start", "selectDepartment", "services", "selectDoctor", "fillForm"];
        const currentIndex = stepOrder.indexOf(step);
        const targetIndex = stepOrder.indexOf(targetStep);
        
        // Only allow going back to previous steps or consultation type at any time
        if (targetIndex < currentIndex || targetStep === "consultationType") {
            setStep(targetStep);
        }
    };

    const appointmentPayload = {
        patient_id: profile?.id,
        appointment_date: moment(`${moment(startDate).format("YYYY-MM-DD")} ${selectedTime?.formattedTime}`,"YYYY-MM-DD hh:mm A").format("YYYY-MM-DD HH:mm:ss"),
        consultation_id: "initial",
        department_id: selectedDept?.id,
        doctor_id: selectedDoctor?.id,
        description: description,
        pre_Consultation: JSON.stringify(selectedComplaints),
        patient: currentUser,
        is_online: isVirtual,
        "service_id": selectedService?.id,
        "isAdvanced": true,
        "consultation": {
          "label": "Regular Encounter/Initial Consultation/Normal Consultation",
          "value": "initial"
        },
        "skipCount": false
    };

    const handleAppointmentSubmit = () => {
        handleBookAppointment(appointmentPayload);
    };

    // Animation variants
    const pageTransition = {
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 20 },
        transition: { duration: 0.3 }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const stepItems = [
        { key: "consultationType", label: "Type", icon: <Video size={16} /> },
        { key: "start", label: "Date", icon: <Calendar size={16} /> },
        { key: "selectDepartment", label: "Department", icon: <Users size={16} /> },
        { key: "services", label: "Service", icon: <Stethoscope size={16} /> },
        { key: "selectDoctor", label: "Doctor", icon: <UserCircle size={16} /> },
        { key: "fillForm", label: "Details", icon: <FileText size={16} /> }
    ];
    const currentStepIndex = Math.max(stepItems.findIndex((item) => item.key === step), 0);
    const progressLabel = `${currentStepIndex + 1} of ${stepItems.length}`;

    // UI Components
    const renderStepIndicator = () => {
        const currentIndex = currentStepIndex;

        return (
            <div className="step-indicator mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    {stepItems.map((s, index) => (
                        <React.Fragment key={s.key}>
                            <div className="step-wrapper">
                                <div 
                                    className={`step-circle 
                                        ${index <= currentIndex ? 'active' : ''} 
                                        ${index < currentIndex ? 'completed' : ''}
                                        ${s.key === "consultationType" && index < currentIndex ? 'editable' : ''}
                                        ${index > currentIndex ? 'disabled' : ''}
                                    `}
                                    data-tooltip={s.label}
                                    onClick={() => {
                                        // Allow clicking on consultation type step at any time
                                        if (s.key === "consultationType") {
                                            handleStepClick(s.key);
                                        }
                                        // Allow clicking on previous steps
                                        else if (index < currentIndex) {
                                            handleStepClick(s.key);
                                        }
                                    }}
                                    style={{ cursor: (index < currentIndex || s.key === "consultationType") ? 'pointer' : 'default' }}
                                >
                                    {index < currentIndex ? <CheckCircle2 size={20} /> : s.icon}
                                    {s.key === "consultationType" && index < currentIndex && (
                                        <span className="edit-badge">
                                            <Edit2 size={12} />
                                        </span>
                                    )}
                                </div>
                                <span className="step-label d-none d-md-block">{s.label}</span>
                            </div>
                            {index < stepItems.length - 1 && (
                                <div className={`step-connector ${index < currentIndex ? 'completed' : ''}`}>
                                    <div className="connector-line"></div>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );
    };

    const renderHero = () => (
        <section className="schedule-hero">
            <div className="schedule-hero-copy">
                <span className="schedule-kicker">Appointments</span>
                <h1>Schedule care in a calmer, clearer flow</h1>
                <p>
                    Choose your consultation type, pick the right department and doctor,
                    and complete your booking with a cleaner experience on desktop and mobile.
                </p>
            </div>

            <div className="schedule-hero-stats">
                <div className="schedule-stat">
                    <small>Consultation</small>
                    <strong>
                        {isVirtual === undefined
                            ? "Choose format"
                            : isVirtual
                            ? "Virtual"
                            : "Physical"}
                    </strong>
                </div>
                <div className="schedule-stat">
                    <small>Selected Date</small>
                    <strong>
                        {startDate
                            ? moment(startDate).format("ddd, MMM D")
                            : "Not set"}
                    </strong>
                </div>
                <div className="schedule-stat">
                    <small>Progress</small>
                    <strong>{progressLabel}</strong>
                </div>
            </div>
        </section>
    );

    const renderConsultationType = () => (
        <motion.div 
            className="consultation-type-selection"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
        >
            <div className="text-center mb-5">
                <h3 className="mb-3">How would you like to consult?</h3>
                <p className="text-muted">Choose your preferred consultation method</p>
            </div>
            
            <Row className="g-4">
                <Col md={6}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Card 
                            className={`consultation-card ${isVirtual === true ? 'selected' : ''}`}
                            onClick={() => { setIsVirtual(true); setStep("start"); }}
                        >
                            <Card.Body className="text-center p-4">
                                <div className="icon-wrapper bg-primary-soft mb-3">
                                    <Video size={40} className="text-primary" />
                                </div>
                                <h5 className="mb-2">Virtual Consultation</h5>
                                <p className="text-muted small mb-3">Consult from the comfort of your home</p>
                                <Badge bg="light" text="dark" className="px-3 py-2">
                                    <Clock size={14} className="me-1" /> Available 24/7
                                </Badge>
                                {isVirtual === true && (
                                    <CheckCircle2 size={24} className="text-success position-absolute top-0 end-0 m-3" />
                                )}
                            </Card.Body>
                        </Card>
                    </motion.div>
                </Col>
                
                <Col md={6}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Card 
                            className={`consultation-card ${isVirtual === false ? 'selected' : ''}`}
                            onClick={() => { setIsVirtual(false); setStep("start"); }}
                        >
                            <Card.Body className="text-center p-4">
                                <div className="icon-wrapper bg-success-soft mb-3">
                                    <MapPin size={40} className="text-success" />
                                </div>
                                <h5 className="mb-2">Physical Consultation</h5>
                                <p className="text-muted small mb-3">Visit our facility for in-person care</p>
                                <Badge bg="light" text="dark" className="px-3 py-2">
                                    <MapPin size={14} className="me-1" /> Available Mon-Fri
                                </Badge>
                                {isVirtual === false && (
                                    <CheckCircle2 size={24} className="text-success position-absolute top-0 end-0 m-3" />
                                )}
                            </Card.Body>
                        </Card>
                    </motion.div>
                </Col>
            </Row>
        </motion.div>
    );

    const renderDateSelection = () => (
        <motion.div 
            className="date-selection"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
        >
            <div className="text-center mb-4">
                <div className="date-header mb-4">
                    <Calendar size={48} className="text-primary mb-3" />
                    <h3>Select a Date</h3>
                    <p className="text-muted">Choose your preferred appointment date</p>
                </div>
                
                <div className="date-picker-wrapper">
                    <ReactDatePicker 
                        minDate={new Date(new Date().setDate(new Date().getDate() + 1))} 
                        showPreviousMonths={false} 
                        onChange={handleDateSelect} 
                        inline 
                        calendarClassName="custom-datepicker"
                    />
                </div>

                <div className="mt-4 d-flex gap-2 justify-content-center">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setStep("consultationType")}
                    >
                        <ArrowLeft size={16} className="me-2" />
                        Change Type
                    </Button>
                </div>
            </div>
        </motion.div>
    );

    const renderDepartmentSelection = () => (
        <motion.div 
            className="department-selection"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={containerVariants}
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Select Department</h3>
                    <p className="text-muted">Choose the department for your consultation</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("consultationType")}>
                        <Edit2 size={14} className="me-2" />
                        Change Type
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("start")}>
                        <ArrowLeft size={16} className="me-2" />
                        Change Date
                    </Button>
                </div>
            </div>

            <Row className="g-3">
                {departments
                    ?.filter((x) => x?.has_appointment === 1 && x?.services?.length > 0)
                    ?.map((item, index) => (
                        <Col key={item.id} xs={12} md={6} lg={4}>
                            <motion.div variants={itemVariants}>
                                <Card 
                                    className={`department-card h-100 ${selectedDept?.id === item.id ? 'selected' : ''}`}
                                    onClick={() => handleDepartmentSelect(item)}
                                >
                                    <Card.Body className="d-flex align-items-center">
                                        <div className="department-icon me-3">
                                            <Users size={24} className="text-primary" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">{item.name}</h6>
                                            <small className="text-muted">
                                                {item.services?.length || 0} Services Available
                                            </small>
                                        </div>
                                        {selectedDept?.id === item.id && (
                                            <CheckCircle2 size={20} className="text-success" />
                                        )}
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
            </Row>
        </motion.div>
    );

    const renderServicesSelection = () => (
        <motion.div 
            className="services-selection"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={containerVariants}
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Select Service</h3>
                    <p className="text-muted">Choose the specific service you need</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("consultationType")}>
                        <Edit2 size={14} className="me-2" />
                        Change Type
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("selectDepartment")}>
                        <ArrowLeft size={16} className="me-2" />
                        Change Department
                    </Button>
                </div>
            </div>

            <Row className="g-3">
                {selectedDept?.services
                    ?.filter(s => !s?.name?.toLowerCase()?.includes('subsequent'))
                    ?.map((service, index) => (
                    <Col key={service?.id} xs={12} md={6}>
                        <motion.div variants={itemVariants}>
                            <Card 
                                className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}
                                onClick={() => handleServiceSelect(service)}
                            >
                                <Card.Body>
                                    <div className="d-flex align-items-start">
                                        <div className="service-icon me-3">
                                            <Stethoscope size={24} className="text-primary" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-2">{service.name}</h6>
                                            <p className="small text-muted mb-2">{service.description}</p>
                                            <Badge bg="light" text="dark">
                                                Duration: {service.duration || '15'} mins
                                            </Badge>
                                        </div>
                                        {selectedService?.id === service.id && (
                                            <CheckCircle2 size={20} className="text-success" />
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>
                ))}
            </Row>
        </motion.div>
    );

    const renderDoctorSelection = () => (
        <motion.div 
            className="doctor-selection"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={containerVariants}
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Select Doctor</h3>
                    <p className="text-muted">Choose your preferred doctor</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("consultationType")}>
                        <Edit2 size={14} className="me-2" />
                        Change Type
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("services")}>
                        <ArrowLeft size={16} className="me-2" />
                        Change Service
                    </Button>
                </div>
            </div>

            {loadingDr ? (
                <div className="text-center py-5">
                    <Spinner1 />
                </div>
            ) : (
                <Row className="g-3">
                    {
                    groupedDoctors?.length === 0 && (
                        <div className="text-center py-5">
                            <AlertCircle size={40} className="text-warning mb-3" />
                            <p className="text-muted small">No doctors available for the selected date and department</p>
                             <p className="text-muted small">Would like us to connect you with an agent?</p>
                            <Button onClick={()=>navigate("/dashboard/support")} variant="outline-primary" size="sm" className="mt-2">
                                <UserCircle size={14} className="me-2" />
                                Conect With Agent
                            </Button>
                        </div>
                    )}
                    {groupedDoctors?.length > 0 && groupedDoctors?.map((doctor, index) => (
                        <Col key={doctor?.id} xs={12}>
                            <motion.div variants={itemVariants}>
                                <DoctorCard 
                                    doctor={doctor} 
                                    isSelected={selectedDoctor?.id === doctor.id}
                                    onSelect={() => handleDoctorSelect(doctor)}
                                    onTimeSelect={setSelectedTime}
                                    setSelectedTime={setSelectedTime}
                                    setSelectedDoctor={setSelectedDoctor}
                                    handleContinueToForm={handleContinueToForm}
                                />
                            </motion.div>
                        </Col>
                    ))}
                </Row>
            )}
        </motion.div>
    );

    const renderAppointmentForm = () => (
        <motion.div 
            className="appointment-form"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Additional Details</h3>
                    <p className="text-muted">Please provide more information about your visit</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep("consultationType")}>
                        <Edit2 size={14} className="me-2" />
                        Change Type
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={handleBack}>
                        <ArrowLeft size={16} className="me-2" />
                        Back
                    </Button>
                </div>
            </div>

            <Wizard 
                loading={booking}
                setDescription={setDescription}
                description={description}
                handleSubmit={handleAppointmentSubmit}
                selectedComplaints={selectedComplaints}
                setSelectedComplaints={setSelectedComplaints}
            />
        </motion.div>
    );

    const renderBookingSummary = () => (
        <Card className="booking-summary">
            <Card.Header className="bg-transparent border-bottom-0 pt-3">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 d-flex align-items-center">
                        <CalendarDays size={18} className="me-2 text-primary" />
                        Appointment Summary
                    </h6>
                    {isVirtual !== undefined && (
                        <Button 
                            variant="light" 
                            size="sm" 
                            className="edit-summary-btn"
                            onClick={() => setStep("consultationType")}
                        >
                            <Edit2 size={14} />
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                {!startDate && !selectedDept && !selectedDoctor && (
                    <div className="text-center py-4">
                        <AlertCircle size={40} className="text-warning mb-3" />
                        <p className=" text-white small">No appointment details yet</p>
                    </div>
                )}
                
                <ul className="summary-list list-unstyled mb-0">
                    {isVirtual !== undefined && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                {isVirtual ? <Video size={16} /> : <MapPin size={16} />}
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Type</span>
                                <strong className="summary-value">
                                    {isVirtual ? 'Virtual Consultation' : 'Physical Consultation'}
                                </strong>
                            </div>
                        </motion.li>
                    )}
                    
                    {startDate && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                <Calendar size={16} className="text-primary" />
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Date</span>
                                <strong className="summary-value">
                                    {moment(startDate).format("dddd, MMMM DD, YYYY")}
                                </strong>
                            </div>
                        </motion.li>
                    )}
                    
                    {selectedDept && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                <Users size={16} className="text-info" />
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Department</span>
                                <strong className="summary-value">{selectedDept?.name}</strong>
                            </div>
                        </motion.li>
                    )}
                    
                    {selectedService && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                <Stethoscope size={16} className="text-success" />
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Service</span>
                                <strong className="summary-value">{selectedService?.name}</strong>
                            </div>
                        </motion.li>
                    )}
                    
                    {selectedDoctor && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                <UserCircle size={16} className="text-warning" />
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Doctor</span>
                                <strong className="summary-value">
                                    Dr. {selectedDoctor?.first_name} {selectedDoctor?.last_name}
                                </strong>
                            </div>
                        </motion.li>
                    )}
                    
                    {selectedTime && (
                        <motion.li 
                            className="summary-item"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="summary-icon">
                                <Clock size={16} className="text-danger" />
                            </div>
                            <div className="summary-content">
                                <span className="summary-label">Time</span>
                                <strong className="summary-value">{selectedTime?.formattedTime}</strong>
                            </div>
                        </motion.li>
                    )}
                </ul>
            </Card.Body>
            <Card.Footer className="bg-transparent border-top-0 pb-3">
                {isVirtual !== undefined && (
                    <Badge 
                        bg={isVirtual ? "primary" : "success"} 
                        className="w-100 py-2"
                    >
                        {isVirtual ? (
                            <><Video size={14} className="me-2" /> Virtual Consultation</>
                        ) : (
                            <><MapPin size={14} className="me-2" /> Physical Consultation</>
                        )}
                    </Badge>
                )}
            </Card.Footer>
        </Card>
    );

    const renderPreviousAppointments = () => (
        <Card className="previous-appointments-card">
            <Card.Header className="bg-transparent border-bottom-0 pt-3">
                <h6 className="mb-0 d-flex align-items-center">
                    <Clock size={18} className="me-2 text-primary" />
                    Recent Appointments
                </h6>
            </Card.Header>
            <Card.Body className="pt-0">
                {onLoad ? (
                    <div className="text-center py-4">
                        <Spinner1 />
                    </div>
                ) : appointments?.length > 0 ? (
                    <motion.div 
                        className="appointments-list"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {appointments?.slice(0, 5)?.map((item, index) => (
                            <motion.div 
                                key={item?.id}
                                variants={itemVariants}
                                className="appointment-item"
                            >
                                <Card className="border-0 bg-light">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <Badge 
                                                bg={
                                                    item?.status === "Cancelled" ? 'danger' :
                                                    item?.status === "Approved" ? 'success' :
                                                    item?.status === 'pending' ? 'warning' : 'secondary'
                                                }
                                                className="px-2 py-1"
                                            >
                                                {item?.status === "Approved" && <CheckCircle2 size={12} className="me-1" />}
                                                {item?.status === "Cancelled" && <XCircle size={12} className="me-1" />}
                                                {item?.status}
                                            </Badge>
                                            <small className="text-muted">
                                                {moment(item?.appointment_date).format('MMM DD, YYYY')}
                                            </small>
                                        </div>
                                        <h6 className="card-title mb-1 text-capitalize fs-sm">
                                            {item?.consultation_type ?? "General Consultation"}
                                        </h6>
                                        <p className="fs-xs text-muted mb-0">
                                            Dr. {item?.whomToSee?.first_name} {item?.whomToSee?.last_name}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="text-center py-4">
                        <Calendar size={32} className="text-muted mb-2" />
                        <p className="text-muted small">No previous appointments</p>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    return (
        <>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin}`}>
                <Container fluid className="px-lg-4">
                    {renderHero()}

                    <Row className="g-4">
                        {/* Left Sidebar - Summary */}
                        <Col xl={3} className="order-xl-1 order-2">
                            <div className="sticky-sidebar">
                                {renderBookingSummary()}
                                <div className="mt-4 d-none d-xl-block">
                                    <Card className="border-0 schedule-side-calendar">
                                        <Card.Body className="p-3">
                                            <small className="text-muted d-block mb-2">Selected Date</small>
                                            <ReactDatePicker 
                                                minDate={new Date(new Date().setDate(new Date().getDate() + 1))} 
                                                showPreviousMonths={false} 
                                                onChange={handleDateSelect} 
                                                selected={startDate}
                                                inline 
                                                calendarClassName="sidebar-datepicker"
                                            />
                                        </Card.Body>
                                    </Card>
                                </div>
                            </div>
                        </Col>

                        {/* Main Content */}
                        <Col xl={6} className="order-xl-2 order-1">
                            <Card className="main-content-card border-0 shadow-sm">
                                <Card.Body className="p-4 p-lg-5">
                                    {renderStepIndicator()}
                                    
                                    <AnimatePresence mode="wait">
                                        {step === "consultationType" && renderConsultationType()}
                                        {step === "start" && renderDateSelection()}
                                        {step === "selectDepartment" && renderDepartmentSelection()}
                                        {step === "services" && renderServicesSelection()}
                                        {step === "selectDoctor" && renderDoctorSelection()}
                                        {step === "fillForm" && renderAppointmentForm()}
                                    </AnimatePresence>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Right Sidebar - Previous Appointments */}
                        <Col xl={3} md={4} className="order-xl-3 order-3 d-none d-md-block">
                            <div className="sticky-sidebar">
                                {renderPreviousAppointments()}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <style jsx>{`
                .schedule-hero {
                    margin-bottom: 24px;
                    padding: 28px;
                    border-radius: 30px;
                    background:
                        radial-gradient(circle at top right, rgba(243, 232, 214, 0.9), transparent 28%),
                        linear-gradient(135deg, rgba(255,255,255,0.96), rgba(233, 241, 236, 0.92));
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    box-shadow: 0 24px 48px rgba(16, 24, 20, 0.08);
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 24px;
                }

                .schedule-hero-copy {
                    max-width: 660px;
                }

                .schedule-kicker {
                    display: inline-flex;
                    margin-bottom: 12px;
                    padding: 8px 12px;
                    border-radius: 999px;
                    background: rgba(59, 159, 113, 0.12);
                    color: #225f43;
                    font-size: 0.72rem;
                    font-weight: 800;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                }

                .schedule-hero h1 {
                    margin-bottom: 10px;
                    font-size: clamp(1.9rem, 3.4vw, 2.75rem);
                    letter-spacing: -0.05em;
                    color: #172017;
                }

                .schedule-hero p {
                    margin-bottom: 0;
                    color: #5f6c60;
                    line-height: 1.75;
                    max-width: 58ch;
                }

                .schedule-hero-stats {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(140px, 1fr));
                    gap: 14px;
                    width: min(100%, 460px);
                }

                .schedule-stat {
                    padding: 18px;
                    border-radius: 22px;
                    background: rgba(255,255,255,0.78);
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    backdrop-filter: blur(8px);
                }

                .schedule-stat small {
                    display: block;
                    margin-bottom: 6px;
                    color: #738176;
                    font-size: 0.72rem;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                }

                .schedule-stat strong {
                    display: block;
                    color: #172017;
                    font-size: 1rem;
                    line-height: 1.5;
                }

                .step-indicator {
                    background: rgba(245, 248, 245, 0.9);
                    border: 1px solid rgba(23, 32, 23, 0.06);
                    border-radius: 24px;
                    padding: 18px 20px;
                    margin-bottom: 2rem !important;
                }
                
                .step-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .step-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.98);
                    border: 2px solid rgba(23, 32, 23, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #738176;
                    transition: all 0.3s ease;
                    position: relative;
                }
                
                .step-circle.active {
                    background: linear-gradient(135deg, #2f7d57 0%, #5fa57c 100%);
                    border-color: #2f7d57;
                    color: white;
                    transform: scale(1.1);
                    box-shadow: 0 0 0 5px rgba(47, 125, 87, 0.12);
                }
                
                .step-circle.completed {
                    background: #245e42;
                    border-color: #245e42;
                    color: white;
                }
                
                .step-circle.editable {
                    cursor: pointer;
                }
                
                .step-circle.editable:hover {
                    transform: scale(1.15);
                    box-shadow: 0 0 0 4px rgba(25, 135, 84, 0.2);
                }
                
                .step-circle.disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .step-circle[data-tooltip]:hover:after {
                    content: attr(data-tooltip);
                    position: absolute;
                    bottom: -30px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: rgba(0,0,0,0.8);
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 1000;
                }
                
                .edit-badge {
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    background: #ffc107;
                    color: #000;
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                }
                
                .step-connector {
                    flex: 1;
                    position: relative;
                    margin: 0 10px;
                }
                
                .connector-line {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: rgba(23, 32, 23, 0.1);
                    transform: translateY(-50%);
                }
                
                .step-connector.completed .connector-line {
                    background: #245e42;
                }
                
                .step-label {
                    font-size: 12px;
                    color: #738176;
                }
                
                .consultation-card {
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    position: relative;
                    overflow: hidden;
                    border-radius: 26px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(246, 249, 246, 0.94));
                    box-shadow: 0 18px 40px rgba(16, 24, 20, 0.06);
                }
                
                .consultation-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 22px 48px rgba(16, 24, 20, 0.1);
                }
                
                .consultation-card.selected {
                    border-color: rgba(47, 125, 87, 0.28);
                    background: linear-gradient(180deg, rgba(244, 251, 247, 0.98), rgba(234, 245, 239, 0.98));
                    box-shadow: 0 24px 52px rgba(47, 125, 87, 0.12);
                }
                
                .icon-wrapper {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                
                .bg-primary-soft {
                    background: rgba(47, 125, 87, 0.1);
                }
                
                .bg-success-soft {
                    background: rgba(207, 150, 61, 0.14);
                }
                
                .department-card, .service-card {
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    border-radius: 24px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247, 249, 247, 0.94));
                    box-shadow: 0 18px 40px rgba(16, 24, 20, 0.05);
                }
                
                .department-card:hover, .service-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 22px 48px rgba(16, 24, 20, 0.1);
                }
                
                .department-card.selected, .service-card.selected {
                    border-color: rgba(47, 125, 87, 0.28);
                    background: linear-gradient(180deg, rgba(244, 251, 247, 0.98), rgba(234, 245, 239, 0.98));
                }
                
                .department-icon, .service-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: rgba(47, 125, 87, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .booking-summary {
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    background:
                        radial-gradient(circle at top right, rgba(243, 232, 214, 0.28), transparent 26%),
                        linear-gradient(180deg, rgba(29, 53, 42, 0.98), rgba(35, 77, 58, 0.98));
                    color: white;
                    overflow: hidden;
                    position: relative;
                    border-radius: 30px;
                    box-shadow: 0 22px 48px rgba(16, 24, 20, 0.16);
                }
                
                .booking-summary::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
                    animation: rotate 20s linear infinite;
                }
                
                @keyframes rotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .booking-summary .card-header,
                .booking-summary .card-footer {
                    background: transparent;
                    border-color: rgba(255,255,255,0.1);
                    position: relative;
                    z-index: 1;
                }
                
                .booking-summary .card-body {
                    position: relative;
                    z-index: 1;
                }
                
                .edit-summary-btn {
                    background: rgba(255,255,255,0.2);
                    border: none;
                    color: white;
                    padding: 0.4rem 0.6rem;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }
                
                .edit-summary-btn:hover {
                    background: rgba(255,255,255,0.3);
                    color: white;
                    transform: scale(1.05);
                }
                
                .summary-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                
                .summary-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }
                
                .summary-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 10px;
                    background: rgba(255,255,255,0.12);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .summary-content {
                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .summary-label {
                    font-size: 11px;
                    opacity: 0.76;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                }
                
                .summary-value {
                    font-size: 14px;
                    font-weight: 600;
                }
                
                .previous-appointments-card {
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    border-radius: 28px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.96), rgba(246, 248, 246, 0.94));
                    box-shadow: 0 18px 40px rgba(16, 24, 20, 0.06);
                }
                
                .appointment-item {
                    margin-bottom: 0.75rem;
                }
                
                .appointment-item .card {
                    transition: all 0.3s ease;
                    border-radius: 20px;
                    background: #f7f8f7 !important;
                }
                
                .appointment-item .card:hover {
                    transform: translateX(-3px);
                    box-shadow: 0 16px 32px rgba(16, 24, 20, 0.08);
                }
                
                .fs-sm {
                    font-size: 0.875rem;
                }
                
                .fs-xs {
                    font-size: 0.75rem;
                }
                
                .sticky-sidebar {
                    position: sticky;
                    top: 24px;
                }
                
                .main-content-card {
                    border-radius: 30px;
                    overflow: hidden;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247, 249, 247, 0.96));
                    box-shadow: 0 24px 48px rgba(16, 24, 20, 0.08);
                    border: 1px solid rgba(23, 32, 23, 0.06);
                }

                .schedule-side-calendar {
                    border-radius: 26px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.96), rgba(247, 249, 247, 0.96)) !important;
                    box-shadow: 0 18px 40px rgba(16, 24, 20, 0.06);
                }
                
                .date-picker-wrapper :global(.custom-datepicker) {
                    border: none;
                    background: transparent;
                    font-family: inherit;
                }
                
                .date-picker-wrapper :global(.react-datepicker) {
                    border: 1px solid rgba(23, 32, 23, 0.08);
                    box-shadow: 0 18px 40px rgba(16, 24, 20, 0.08);
                    border-radius: 24px;
                    overflow: hidden;
                }
                
                .date-picker-wrapper :global(.react-datepicker__header) {
                    background: linear-gradient(135deg, #204d38 0%, #3f7d5c 100%);
                    color: white;
                    border-bottom: none;
                    padding-top: 18px;
                }
                
                .date-picker-wrapper :global(.react-datepicker__current-month) {
                    color: white;
                }
                
                .date-picker-wrapper :global(.react-datepicker__day-name) {
                    color: rgba(255,255,255,0.7);
                }
                
                .date-picker-wrapper :global(.react-datepicker__day--selected) {
                    background: linear-gradient(135deg, #204d38 0%, #3f7d5c 100%);
                    color: white;
                    border-radius: 50%;
                }
                
                .sidebar-datepicker :global(.react-datepicker) {
                    border: none;
                    background: transparent;
                }
                
                .sidebar-datepicker :global(.react-datepicker__header) {
                    background: transparent;
                    border-bottom: none;
                }
                
                .sidebar-datepicker :global(.react-datepicker__day) {
                    margin: 0.2rem;
                }
                
                @media (max-width: 1199px) {
                    .sticky-sidebar {
                        position: static;
                    }
                }

                @media (max-width: 991px) {
                    .schedule-hero {
                        padding: 22px;
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .schedule-hero-stats {
                        width: 100%;
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 767px) {
                    .schedule-hero {
                        padding: 20px;
                        border-radius: 24px;
                        margin-bottom: 18px;
                    }

                    .schedule-hero h1 {
                        font-size: 1.9rem;
                    }

                    .step-indicator {
                        padding: 14px 12px;
                        overflow-x: auto;
                    }

                    .step-wrapper {
                        min-width: 56px;
                    }

                    .step-connector {
                        min-width: 24px;
                    }

                    .main-content-card,
                    .booking-summary,
                    .previous-appointments-card,
                    .schedule-side-calendar,
                    .consultation-card,
                    .department-card,
                    .service-card {
                        border-radius: 22px;
                    }

                    .main-content-card .card-body {
                        padding: 1.25rem !important;
                    }

                    .consultation-card .card-body {
                        padding: 1.5rem !important;
                    }

                    .date-header {
                        margin-bottom: 1.25rem !important;
                    }

                    .date-picker-wrapper :global(.react-datepicker) {
                        width: 100%;
                    }

                    .date-picker-wrapper :global(.react-datepicker__month-container) {
                        width: 100%;
                    }

                    .date-picker-wrapper :global(.react-datepicker__day-name),
                    .date-picker-wrapper :global(.react-datepicker__day) {
                        width: 2rem;
                        line-height: 2rem;
                        margin: 0.18rem;
                    }

                    .appointment-form .d-flex,
                    .doctor-selection .d-flex,
                    .services-selection .d-flex,
                    .department-selection .d-flex {
                        flex-wrap: wrap;
                        gap: 0.75rem;
                    }
                }
                
                .btn-outline-secondary {
                    border-color: #dee2e6;
                    color: #6c757d;
                }
                
                .btn-outline-secondary:hover {
                    background: #f8f9fa;
                    border-color: #dee2e6;
                    color: #495057;
                }
            `}</style>
        </>
    );
};

export default Schedule;
